import { Divider, ScrollArea, Stack, Tooltip } from '@mantine/core';
import DatasetsSection from '@/pages/Result/components/DatasetsSection.tsx';
import RunSelectMenu from '@/pages/Result/components/RunSelectMenu.tsx';
import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets.ts';
import React from 'react';
import { HiArrowDownTray } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import useDownloadRunFilesZip from '@/hooks/api/runs/useDownloadRunFilesZip.ts';
import { RunWithDatasets } from '@/types/runs.ts';

type props = {
  runWithDatasets: RunWithDatasets;
  addTab: (datasetId: string) => void;
};
const SIDEBAR_WIDTH = 250;

export function RunDatasetsSidebar({ runWithDatasets, addTab }: props) {
  const { data: staticDatasets } = useRetrieveStaticDatasets();

  const { mutate: downloadFiles, isPending: downloadingFiles } = useDownloadRunFilesZip(
    runWithDatasets.run,
  );

  const downloadDisabled =
    runWithDatasets.output_datasets.length === 0 && runWithDatasets.input_datasets.length === 0;

  return (
    <Stack
      w={SIDEBAR_WIDTH}
      style={{ borderRight: '1px solid #e0e0e0', flexShrink: 0 }}
      gap={'0'}
      pos={'relative'}
    >
      <RunSelectMenu run={runWithDatasets.run} />

      <Divider mt="0" mb={'0'} />

      <Tooltip
        label={downloadDisabled ? 'No files to download' : 'Download all'}
        position={downloadDisabled ? 'top' : 'bottom'}
        openDelay={250}
      >
        <StyledButton
          variant="outline"
          size="sm"
          maw={'22px'}
          mah={'20px'}
          p={'4px'}
          style={{
            borderRadius: '5px',
            position: 'absolute',
            top: '54px',
            right: '8px',
            zIndex: 10,
          }}
          loading={downloadingFiles}
          onClick={() => downloadFiles({ filesType: 'all' })}
          loaderProps={{ size: 12 }}
          disabled={downloadDisabled}
        >
          <AppIcon Icon={HiArrowDownTray} size={12} c={'black'} />
        </StyledButton>
      </Tooltip>
      <ScrollArea pt={'xs'} scrollbars={'y'}>
        <Stack px={'xs'} py={'md'}>
          <DatasetsSection
            run={runWithDatasets.run}
            title={'Output'}
            datasets={runWithDatasets.output_datasets.map((o) => o.dataset)}
            addTab={addTab}
          />
          <DatasetsSection
            run={runWithDatasets.run}
            title={'Input'}
            datasets={runWithDatasets.input_datasets.map((i) => i.dataset)}
            addTab={addTab}
          />
          <DatasetsSection
            run={runWithDatasets.run}
            title={'Knowledge Source'}
            datasets={staticDatasets?.results.map((s) => s.dataset) || []}
            addTab={addTab}
          />
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
